import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { myFilesReminders, myFilesFolderView, myFilesRemindersDelete } from '../../../../../services/CommonService';
import { toast } from 'react-toastify';

const ReminderModal = ({ show, onHide, file, userId, folderId, setReminderStatus, }) => {
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [description, setDescription] = useState('');
  const [reminderSettings, setReminderSettings] = useState({
    one_day_before: false,
    one_week_before: false,
    one_month_before: false,
    custom_frequency: false,
    custom_frequency_type: 'daily',
    custom_frequency_days: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const eventDateInputRef = useRef(null);

  // Initialize form fields with existing reminder data when modal opens
  useEffect(() => {
    if (show && file?.reminder) {
      setEventName(file.reminder.event_name || '');
      // Convert event_date to YYYY-MM-DD format for the date input
      if (file.reminder.event_date) {
        const [day, month, year] = file.reminder.event_date.split('/');
        setEventDate(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      } else {
        setEventDate('');
      }
      setDescription(file.reminder.description || '');
      setReminderSettings({
        one_day_before: file.reminder.reminder_settings?.one_day_before || false,
        one_week_before: file.reminder.reminder_settings?.one_week_before || false,
        one_month_before: file.reminder.reminder_settings?.one_month_before || false,
        custom_frequency: file.reminder.reminder_settings?.custom_frequency || false,
        custom_frequency_type: file.reminder.reminder_settings?.custom_frequency_type || 'daily',
        custom_frequency_days: file.reminder.reminder_settings?.custom_frequency_days || '',
      });
    } else {
      // Reset form fields when modal is closed or no reminder exists
      setEventName('');
      setEventDate('');
      setDescription('');
      setReminderSettings({
        one_day_before: false,
        one_week_before: false,
        one_month_before: false,
        custom_frequency: false,
        custom_frequency_type: 'daily',
        custom_frequency_days: '',
      });
      setErrors({});
    }
  }, [show, file]);

  const validateForm = () => {
    const newErrors = {};
    if (!eventName.trim()) newErrors.eventName = 'Event name is required';
    if (!eventDate) newErrors.eventDate = 'Event date is required';
    if (
      !reminderSettings.one_day_before &&
      !reminderSettings.one_week_before &&
      !reminderSettings.one_month_before &&
      !reminderSettings.custom_frequency
    ) {
      newErrors.reminderSettings = 'At least one reminder setting must be selected';
    }
    if (reminderSettings.custom_frequency && !reminderSettings.custom_frequency_days) {
      newErrors.customFrequency = 'Custom frequency days are required when custom frequency is selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const refreshFolderView = async () => {
    try {
      const response = await myFilesFolderView({
        user_id: userId,
        folder_id: folderId
      });
      // You can handle the response data here if needed
      console.log('Folder view refreshed:', response.data);
    } catch (err) {
      console.error('Error refreshing folder view:', err);
      // You might want to show a toast error here if the refresh fails
      toast.error('Failed to refresh file list');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        user_id: userId,
        file_id: file.id,
        event_name: eventName,
        event_date: new Date(eventDate).toLocaleDateString('en-GB'),
        description,
        reminder_settings: reminderSettings,
      };

      const response = await myFilesReminders(payload);

      console.log('Reminder Response:', response.data);

      let newReminder = response.data.reminder;
      if (!newReminder) {
        // fallback: fetch latest file data from folder view
        try {
          const fileResp = await myFilesFolderView({ user_id: userId, folder_id: folderId });
          const updatedFile = fileResp.data.data.files.find(f => f.id === file.id);
          newReminder = updatedFile?.reminder;
        } catch (e) {
          // fallback failed, leave as undefined
        }
      }

      if (response.data.message?.toLowerCase().includes('success')) {
        toast.success(file.reminder ? 'Reminder updated successfully' : 'Reminder set successfully');
        setReminderStatus(file.id, true, newReminder); // send new reminder data

        // Call myFilesFolderView to refresh file list
        await refreshFolderView();

        onHide(); // Close modal
      } else {
        throw new Error('Failed to set/update reminder');
      }
    } catch (err) {
      console.error('Error setting/updating reminder:', err);
      toast.error('Reminder already exists for this file or failed to set/update');
    } finally {
      setLoading(false);
    }
  };


  const handleDelete = async () => {
    setLoading(true);
    try {
      const payload = {
        file_id: file.id,
      };

      const response = await myFilesRemindersDelete(payload);

      if (response.data.message?.toLowerCase().includes('success')) {
        toast.success('Reminder deleted successfully');
        setReminderStatus(file.id, false, null); // send null reminder data

        // Call myFilesFolderView to refresh file list
        await refreshFolderView();

        onHide(); // Close modal
      } else {
        throw new Error('Failed to delete reminder');
      }
    } catch (err) {
      console.error('Error deleting reminder:', err);
      toast.error(err.response?.data?.message || 'Failed to delete reminder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <Modal
        show={show}
        onHide={onHide}
        centered
        className="enhanced-modal"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="modal-header-custom">
          <Modal.Title className="modal-title-custom">
            <span
              className="file-name-truncate"
              data-bs-toggle="tooltip"
              data-bs-title={file?.file_name}
            >
              {file?.file_name}
            </span>
          </Modal.Title>
          <Button
            variant="link"
            onClick={onHide}
            disabled={loading}
            className="btn-close-custom"
            aria-label="Close"
          >
            <i className="bi bi-x-lg" />
          </Button>
        </Modal.Header>

        <Modal.Body className="modal-body-custom">
          <Form>
            {/* Event Name */}
            <Form.Group className="mb-2">
              <Form.Label className="form-label-custom">
                Event Name<span className="text-danger">*</span>
                <i
                  className="bi bi-info-circle ms-2 text-primary cursor-pointer"
                  data-bs-toggle="tooltip"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Enter a short title for your event."
                />
              </Form.Label>
              <Form.Control
                type="text"
                className={`form-control-custom ${errors.eventName ? 'is-invalid' : ''}`}
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Enter event name"
                disabled={loading}
              />
              {errors.eventName && (
                <div className="invalid-feedback-custom">{errors.eventName}</div>
              )}
            </Form.Group>

            {/* Event Date */}
            <Form.Group
              className="mb-2"
              onClick={() => {
                if (eventDateInputRef.current) {
                  if (eventDateInputRef.current.showPicker) {
                    eventDateInputRef.current.showPicker();
                  } else {
                    eventDateInputRef.current.focus();
                  }
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <Form.Label className="form-label-custom">
                Event Date<span className="text-danger">*</span>
                <i
                  className="bi bi-info-circle ms-2 text-primary cursor-pointer"
                  data-bs-toggle="tooltip"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Select the date when the event will happen"
                />
              </Form.Label>
              <Form.Control
                type="date"
                ref={eventDateInputRef}
                className={`form-control-custom cursor-pointer ${errors.eventDate ? 'is-invalid' : ''}`}
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                disabled={loading}
              />
              {errors.eventDate && (
                <div className="invalid-feedback-custom">{errors.eventDate}</div>
              )}
            </Form.Group>

            {/* Description */}
            <Form.Group className="mb-2">
              <Form.Label className="form-label-custom">
                Description
                <i
                  className="bi bi-info-circle ms-2 text-primary cursor-pointer"
                  data-bs-toggle="tooltip"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Add any additional details or notes about the event."
                />
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="form-control-custom"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter description"
                disabled={loading}
              />
            </Form.Group>

            {/* Reminder Settings */}
            <Form.Group className="mb-2">
              <Form.Label className="form-label-custom">
                Reminder Settings<span className="text-danger">*</span>
                <i
                  className="bi bi-info-circle ms-2 text-primary cursor-pointer"
                  data-bs-toggle="tooltip"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Choose when you want to receive reminders before the event."
                />
              </Form.Label>

              <div className="form-check-custom d-flex row">
                <div className="col-md-6">
                  <Form.Check
                    type="checkbox"
                    label="1 Day Before"
                    checked={reminderSettings.one_day_before}
                    onChange={(e) =>
                      setReminderSettings({
                        ...reminderSettings,
                        one_day_before: e.target.checked,
                      })
                    }
                    disabled={loading}
                  />
                  <Form.Check
                    type="checkbox"
                    label="1 Week Before"
                    checked={reminderSettings.one_week_before}
                    onChange={(e) =>
                      setReminderSettings({
                        ...reminderSettings,
                        one_week_before: e.target.checked,
                      })
                    }
                    disabled={loading}
                  />
                </div>
                <div className="col-md-6">
                  <Form.Check
                    type="checkbox"
                    label="1 Month Before"
                    checked={reminderSettings.one_month_before}
                    onChange={(e) =>
                      setReminderSettings({
                        ...reminderSettings,
                        one_month_before: e.target.checked,
                      })
                    }
                    disabled={loading}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Custom"
                    checked={reminderSettings.custom_frequency}
                    onChange={(e) =>
                      setReminderSettings({
                        ...reminderSettings,
                        custom_frequency: e.target.checked,
                      })
                    }
                    disabled={loading}
                  />
                </div>
              </div>

              {errors.reminderSettings && (
                <div className="invalid-feedback-custom d-block">
                  {errors.reminderSettings}
                </div>
              )}

              {/* Custom Frequency Section */}
              {reminderSettings.custom_frequency && (
                <div className="custom-frequency-section mt-2">
                  <div className="row align-items-center">
                    <div className="col-md-6 position-relative">
                      <div className="d-flex align-items-center">
                        <Form.Select
                          className="form-control-custom mb-2 flex-grow-1"
                          value={reminderSettings.custom_frequency_type}
                          onChange={(e) =>
                            setReminderSettings({
                              ...reminderSettings,
                              custom_frequency_type: e.target.value,
                            })
                          }
                          disabled={loading}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Form.Select>
                        <i
                          className="bi bi-info-circle text-primary ms-2"
                          data-bs-toggle="tooltip"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Choose how often you want to receive reminders — every day, every week, or every month."
                          style={{ fontSize: '18px', cursor: 'pointer' }}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 position-relative">
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          className={`form-control-custom mb-2 flex-grow-1 ${errors.customFrequency ? 'is-invalid' : ''}`}
                          value={reminderSettings.custom_frequency_days}
                          onChange={(e) =>
                            setReminderSettings({
                              ...reminderSettings,
                              custom_frequency_days: parseInt(e.target.value) || '',
                            })
                          }
                          placeholder="Enter frequency days"
                          min="1"
                          disabled={loading}
                        />
                        <i
                          className="bi bi-info-circle text-primary ms-2"
                          data-bs-toggle="tooltip"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="Enter how many days before the event you want to start getting reminders."
                          style={{ fontSize: '18px', cursor: 'pointer' }}
                        />
                      </div>
                      {errors.customFrequency && (
                        <div className="invalid-feedback-custom">{errors.customFrequency}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="modal-footer-custom">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading}
            className="btn-cancel-custom"
          >
            Cancel
          </Button>
          {file?.reminder && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={loading}
              className="btn-submit-custom"
            >
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-submit-custom"
          >
            {file?.reminder ? 'Update' : 'Set Reminder'}
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
};

export default ReminderModal;